import { CheckCircle } from "@phosphor-icons/react";

import { formatPrice } from "features/subscriptions/utils";

import SimpleDataTable from "common/components/SimpleDataTable";
import { Tag } from "common/components/ui/Tag";

import { BILLING_STATUSES } from "../../constants";
import { formatBillingDate } from "../../utils";
import DownloadInvoice from "./DownloadInvoice";

import type { ColumnDef } from "@tanstack/react-table";
import type { Invoice } from "../../types";

const columns: ColumnDef<Invoice>[] = [
  {
    id: "date",
    header: "Date",
    cell: ({ row }) => (
      <span className="text-sm-bold">
        {formatBillingDate(row.original.date * 1000)}
      </span>
    ),
  },
  {
    id: "description",
    header: "Description",
    cell: ({ row }) => {
      const { line_items } = row.original;

      const seatsLineItem = line_items.find(
        ({ entity_type }) => entity_type === "addon_item_price",
      );

      // line items can be an empty array
      let plan = "-";
      if (line_items.length) {
        const mainLineItem = line_items.find(
          ({ entity_type }) => entity_type === "plan_item_price",
        );
        const adhocLineItem = line_items.find(
          ({ entity_type }) => entity_type === "adhoc",
        );
        plan =
          mainLineItem?.description ||
          seatsLineItem?.description ||
          adhocLineItem?.description;
      }

      return plan;
    },
  },
  {
    id: "seats",
    header: "Number of seats",
    cell: ({ row }) => {
      const seatsLineItem = row.original.line_items.find(
        ({ entity_type }) => entity_type === "addon_item_price",
      );

      return seatsLineItem ? seatsLineItem.quantity : "-";
    },
  },
  {
    id: "total",
    header: "Total amount",
    cell: ({ row }) => (
      <span className="text-button-xs">{formatPrice(row.original.total)}</span>
    ),
  },
  {
    id: "status",
    header: "Payment status",
    cell: ({ row }) => {
      const { status } = row.original;

      return (
        <Tag
          variant="tertiary"
          leftIcon={
            status === "paid" && (
              <CheckCircle className="text-green-500" weight="fill" />
            )
          }
          size="md"
        >
          {BILLING_STATUSES[status].label}
        </Tag>
      );
    },
  },
  {
    id: "actions",
    header: "",
    cell: ({ row }) => <DownloadInvoice invoiceId={row.original.id} />,
  },
];

export default function DesktopTable({ invoices }: { invoices: Invoice[] }) {
  return (
    <SimpleDataTable
      columns={columns}
      data={invoices}
      localStorageId="invoices"
    />
  );
}
